import type { StorageBackup, StorageDisk, StorageHost } from '@/db/api'
import type { Health } from './colour'

/**
 * The Infrastructure page's Storage part (components/Storage.tsx), pure: how
 * full a disk is, how its bar is drawn, sizes in words, and whether a host or
 * a backup needs a look. The lines match internal/storage's warnings.
 */

/** internal/storage FullAt and CriticalAt */
export const FULL_AT = 0.85
export const CRITICAL_AT = 0.95

export type Fill = 'ok' | 'warn' | 'critical'

/** The share of a disk in use, 0..1; a disk with no fixed size is 0. */
export const shareOf = (d: Pick<StorageDisk, 'size' | 'used'>): number =>
  d.size > 0 ? Math.min(1, Math.max(0, d.used / d.size)) : 0

export const fillOf = (share: number): Fill =>
  share >= CRITICAL_AT ? 'critical' : share >= FULL_AT ? 'warn' : 'ok'

/** Whole percent, rounded down as the server's warnings are. */
export const pctOf = (share: number): number => Math.floor(share * 100)

const UNITS = ['B', 'kB', 'MB', 'GB', 'TB', 'PB']

/** Bytes in decimal units, as drives are sold: 483 GB, 1.9 TB, 1 TB. */
export const fmtBytes = (n: number): string => {
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  let v = n
  let u = 0
  while (v >= 999.5 && u < UNITS.length - 1) {
    v /= 1000
    u++
  }
  const s = v < 9.95 && u > 0 ? v.toFixed(1).replace(/\.0$/, '') : String(Math.round(v))
  return `${s} ${UNITS[u]}`
}

/** "483 GB of 1 TB", or the size alone for a store with no fixed size. */
export const usedWords = (d: Pick<StorageDisk, 'size' | 'used'>): string =>
  d.size > 0 ? `${fmtBytes(d.used)} of ${fmtBytes(d.size)}` : fmtBytes(d.used)

/** A host needs a look when it does not answer, a disk is past FULL_AT or a drive fails SMART. */
export const hostHealth = (h: StorageHost): Health => {
  if (h.down) return 'warn'
  if (h.disks.some((d) => d.smart === 'failing' || fillOf(shareOf(d)) !== 'ok')) return 'warn'
  if (h.drives?.some((d) => !d.healthy)) return 'warn'
  return 'ok'
}

export const backupHealth = (b: StorageBackup): Health => (b.stale || b.offsite_stale ? 'warn' : 'ok')
